import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { env } from '../../../config/env';
import { useSesionStore } from '../../stores/sesionStore';
import { rutaPublica } from '../../../config/recursos';
import { Logo } from './Logo';
import { repositorioIsotipos } from '../../../data/repositories/RepositorioLogos';
import { LogoDeCliente } from '../../pages/inventario/LogoDeCliente';
import estilos from './MarcaLateral.module.css';

/**
 * Marca en la cabecera de la barra lateral, igual que en la versión anterior:
 *
 *   1. El logotipo de VenIoT, siempre. Si el cliente tiene logo propio, VenIoT
 *      pasa a su versión horizontal (más baja) para dejarle el sitio.
 *   2. Debajo, el logotipo del cliente, si lo tiene.
 *   3. Debajo, «Superadmin» o el nombre del cliente.
 *
 * VenIoT nunca desaparece: es la marca del producto; la del cliente se suma,
 * no lo sustituye.
 *
 * Triple pulsación sobre el logotipo de VenIoT: abre el formulario para
 * cambiar el logo del cliente, igual que en la versión anterior. Lo pueden
 * usar el superadministrador y el administrador del cliente, siempre que la
 * sesión pertenezca a un cliente concreto.
 */
interface MarcaLateralProps {
  /** Plegada: sólo caben los isotipos, sin nombre ni rótulo. */
  plegada?: boolean;
}

export function MarcaLateral({ plegada = false }: MarcaLateralProps) {
  const perfil = useSesionStore((s) => s.perfil);
  const esSuperadmin = perfil?.rol.esSuperadmin ?? false;
  const clienteId = perfil?.clienteId ?? null;

  const [falloLogo, setFalloLogo] = useState(false);
  const [falloIsotipo, setFalloIsotipo] = useState(false);

  // Forma parte de la dirección del isotipo; ver `cerrarEditor`.
  const [versionIsotipo, setVersionIsotipo] = useState(0);
  const [editorAbierto, setEditorAbierto] = useState(false);

  // Marcas de tiempo de las últimas pulsaciones, para detectar la triple.
  const pulsaciones = useRef<number[]>([]);

  const puedeEditarLogo =
    clienteId !== null && (esSuperadmin || (perfil?.rol.esAdminCliente ?? false));

  const alPulsarLogo = () => {
    if (!puedeEditarLogo) return;

    const ahora = Date.now();
    pulsaciones.current = pulsaciones.current.filter((t) => ahora - t <= 600);
    pulsaciones.current.push(ahora);

    if (pulsaciones.current.length >= 3) {
      pulsaciones.current = [];
      setEditorAbierto(true);
    }
  };

  /*
   * Al cerrar el editor se vuelve a intentar cargar el isotipo.
   *
   * Su dirección es siempre la misma, así que ni el navegador ni el componente
   * tienen por qué enterarse de que ahora sí existe: el primer intento falló
   * —no había ninguno— y ese fallo se recordaba el resto de la sesión. Sin
   * esto, quien acababa de subirlo tenía que recargar la página para verlo.
   */
  const cerrarEditor = () => {
    setEditorAbierto(false);
    setFalloIsotipo(false);
    setVersionIsotipo((n) => n + 1);
  };

  const logo = useQuery({
    queryKey: ['cliente', 'logo', clienteId],
    queryFn: () => repositorioCatalogos.logoDeCliente(clienteId),
    enabled: clienteId !== null,
    staleTime: 30 * 60_000,
  });

  // El nombre del cliente para el rótulo; la lista ya viene acotada por RLS.
  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    enabled: !esSuperadmin && clienteId !== null,
    staleTime: 5 * 60_000,
  });

  // Un logo recién subido merece volver a intentarse aunque el anterior fallara.
  useEffect(() => {
    setFalloLogo(false);
  }, [logo.data]);

  const urlLogoCliente = !falloLogo ? (logo.data ?? null) : null;

  /*
   * El isotipo del cliente, si lo subió. No se comprueba si existe: se intenta
   * cargar y, si falla, se queda sólo el de VenIoT, que es la salida correcta
   * tanto si no lo tiene como si el fichero se perdió.
   */
  const urlIsotipoCliente =
    clienteId !== null && !falloIsotipo
      ? `${repositorioIsotipos.direccion(clienteId)}&r=${versionIsotipo}`
      : null;
  const nombreCliente = clientes.data?.find((c) => c.id === clienteId)?.nombre ?? null;

  const rotulo = esSuperadmin ? 'Superadmin' : (nombreCliente ?? env.marca.nombreApp);

  if (plegada) {
    return (
      <>
        <span
          className={puedeEditarLogo ? estilos.zonaEditable : undefined}
          title={rotulo}
          onClick={alPulsarLogo}
        >
          {/*
            El isotipo azul de la marca, no la variante que elige el tema.
            La barra lateral es oscura con los dos temas, y con el claro puesto
            `Logo` serviría la versión navy, que sobre ese fondo no se ve.
          */}
          <img
            src={rutaPublica('logos/logotipo-original.png')}
            alt="VenIoT"
            className={estilos.isotipoVeniot}
          />
        </span>

        {urlIsotipoCliente && (
          <img
            src={urlIsotipoCliente}
            alt={nombreCliente ?? 'Isotipo del cliente'}
            className={estilos.isotipoCliente}
            title={rotulo}
            onError={() => setFalloIsotipo(true)}
          />
        )}

        <LogoDeCliente
          abierto={editorAbierto}
          clienteId={clienteId}
          clienteNombre={nombreCliente ?? rotulo}
          onCerrar={cerrarEditor}
        />
      </>
    );
  }

  return (
    <>
      <span
        className={puedeEditarLogo ? estilos.zonaEditable : undefined}
        title={puedeEditarLogo ? 'Triple pulsación para cambiar el logo del cliente' : undefined}
        onClick={alPulsarLogo}
      >
        <Logo
          variante={urlLogoCliente ? 'largo' : 'centrado'}
          ancho={160}
          className={estilos.logoVeniot}
        />
      </span>

      {urlLogoCliente && (
        <img
          src={urlLogoCliente}
          alt={nombreCliente ?? 'Logotipo del cliente'}
          className={estilos.logoCliente}
          onError={() => setFalloLogo(true)}
        />
      )}

      <h1 className={estilos.rotulo}>{rotulo}</h1>

      <LogoDeCliente
        abierto={editorAbierto}
        clienteId={clienteId}
        clienteNombre={nombreCliente ?? rotulo}
        onCerrar={cerrarEditor}
      />
    </>
  );
}
